// Image fields: a standard push button with an icon (/MK /I), which other PDF tools also treat as an image field, with the picture drawn into
// its appearance (/AP /N) fitted to the box, in a square, rounded or circular frame. The field's own settings (fit, shape, placeholder,
// limits) are kept in a private /FGImg dictionary other tools ignore. TypeScript twin of rd_pdf::imagefield: same keys, same appearance.

import { Dict, PName, PRef, type PObj } from "./parse";
import { pdfText, num } from "./form";
import type { UpdateCtx } from "./incremental";
import { addAcroFields, addPageAnnots } from "./acro";
import { PICK_IMAGE_JS, applyEvents, type EventMap } from "./fieldactions";
import { setWidgetLink, type DataLink } from "./datalink";

export type Fit = "fit" | "fill" | "stretch";
export type Shape = "square" | "rounded" | "circle";

export const FITS: Array<{ id: Fit; label: string }> = [{ id: "fit", label: "Fit inside" }, { id: "fill", label: "Fill the box" }, { id: "stretch", label: "Stretch" }];
export const SHAPES: Array<{ id: Shape; label: string }> = [{ id: "square", label: "Square corners" }, { id: "rounded", label: "Rounded corners" }, { id: "circle", label: "Circle" }];

export interface ImageFieldOpts {
    fit: Fit;
    shape: Shape;
    /** The words shown while no picture is in (empty: "Click to add photo"). */
    placeholder: string;
    /** The largest picture file accepted, in bytes (0: no limit). */
    maxBytes: number;
    /** The file types accepted, as extensions without a dot (empty: png, jpg, bmp). */
    types: string[];
    /** A picture wider or taller than this many pixels is brought down to it (0: kept as it is). */
    maxPx: number;
    border: [number, number, number] | null;
}

export const defaultImageOpts = (): ImageFieldOpts => ({ fit: "fit", shape: "square", placeholder: "", maxBytes: 0, types: [], maxPx: 1600, border: [0.45, 0.5, 0.6] });

export const placeholderText = (o: ImageFieldOpts): string => (o.placeholder.trim() ? o.placeholder : "Click to add photo");

const extKind = (e: string) => { const x = e.replace(/^\./, "").toLowerCase(); return x === "jpeg" ? "jpg" : x; };
export function allowedType(o: ImageFieldOpts, ext: string): boolean {
    const e = extKind(ext);
    return o.types.length === 0 ? e === "png" || e === "jpg" || e === "bmp" : o.types.some((t) => extKind(t) === e);
}

/** Why a picture file is refused by the field's limits, or null. */
export function checkPictureFile(o: ImageFieldOpts, fileName: string, size: number): string | null {
    const ext = fileName.includes(".") ? fileName.slice(fileName.lastIndexOf(".") + 1) : "";
    if (ext && !allowedType(o, ext)) return `This field takes ${o.types.length ? o.types.join(", ") : "png, jpg, bmp"} pictures, not .${ext}`;
    if (o.maxBytes > 0 && size > o.maxBytes) return `The picture is ${Math.ceil(size / 1024)} KB; this field takes at most ${Math.ceil(o.maxBytes / 1024)} KB`;
    return null;
}

// ---- pixels -----------------------------------------------------------------------------------------------------------------------

export interface Pixels { w: number; h: number; rgba: Uint8Array }

/** Brings pixels down so neither side is longer than `max` (a box average, as the desktop Reader does); unchanged when they already fit. */
export function downscale(p: Pixels, max: number): Pixels {
    const { w, h, rgba } = p;
    if (max === 0 || (w <= max && h <= max)) return p;
    const k = max / Math.max(w, h);
    const nw = Math.max(1, Math.round(w * k)), nh = Math.max(1, Math.round(h * k));
    const out = new Uint8Array(nw * nh * 4);
    for (let y = 0; y < nh; y++) {
        const y0 = Math.floor(y / k), y1 = Math.max(Math.min(Math.ceil((y + 1) / k), h), y0 + 1);
        for (let x = 0; x < nw; x++) {
            const x0 = Math.floor(x / k), x1 = Math.max(Math.min(Math.ceil((x + 1) / k), w), x0 + 1);
            const acc = [0, 0, 0, 0];
            let n = 0;
            for (let sy = y0; sy < Math.min(y1, h); sy++) for (let sx = x0; sx < Math.min(x1, w); sx++) { const i = (sy * w + sx) * 4; for (let c = 0; c < 4; c++) acc[c]! += rgba[i + c]!; n++; }
            const o = (y * nw + x) * 4;
            for (let c = 0; c < 4; c++) out[o + c] = Math.floor(acc[c]! / Math.max(1, n));
        }
    }
    return { w: nw, h: nh, rgba: out };
}

/** Cuts the picture down to `crop` (fractions of its width and height: x0, y0 from the top-left, x1, y1). Null when the crop is too small. */
export function cropPixels(p: Pixels, crop: [number, number, number, number]): Pixels | null {
    const cl = (v: number) => Math.min(1, Math.max(0, v));
    const x0 = Math.floor(cl(crop[0]) * p.w), y0 = Math.floor(cl(crop[1]) * p.h), x1 = Math.ceil(cl(crop[2]) * p.w), y1 = Math.ceil(cl(crop[3]) * p.h);
    if (x1 <= x0 + 1 || y1 <= y0 + 1) return null;
    const cw = x1 - x0, ch = y1 - y0;
    const out = new Uint8Array(cw * ch * 4);
    for (let y = y0; y < y1; y++) out.set(p.rgba.subarray((y * p.w + x0) * 4, (y * p.w + x1) * 4), (y - y0) * cw * 4);
    return { w: cw, h: ch, rgba: out };
}

/** Makes the pixels whose colour is close to the colour at the picture's corner (the backdrop) see-through: a simple cut-out. */
export function cutOutBackdrop(p: Pixels, tolerance = 40): Pixels {
    const out = new Uint8Array(p.rgba);
    const corners = [0, (p.w - 1) * 4, (p.h - 1) * p.w * 4, ((p.h - 1) * p.w + p.w - 1) * 4];
    const bg = [0, 1, 2].map((c) => Math.round(corners.reduce((s, i) => s + p.rgba[i + c]!, 0) / 4));
    // flood from the edges so a similar colour inside the subject is kept
    const seen = new Uint8Array(p.w * p.h);
    const stack: number[] = [];
    const near = (i: number) => Math.abs(p.rgba[i * 4]! - bg[0]!) + Math.abs(p.rgba[i * 4 + 1]! - bg[1]!) + Math.abs(p.rgba[i * 4 + 2]! - bg[2]!) <= tolerance * 3 / 2;
    const push = (x: number, y: number) => { const i = y * p.w + x; if (x < 0 || y < 0 || x >= p.w || y >= p.h || seen[i] || !near(i)) return; seen[i] = 1; stack.push(i); };
    for (let x = 0; x < p.w; x++) { push(x, 0); push(x, p.h - 1); }
    for (let y = 0; y < p.h; y++) { push(0, y); push(p.w - 1, y); }
    while (stack.length) { const i = stack.pop()!; const x = i % p.w, y = (i / p.w) | 0; out[i * 4 + 3] = 0; push(x + 1, y); push(x - 1, y); push(x, y + 1); push(x, y - 1); }
    return { w: p.w, h: p.h, rgba: out };
}

// ---- the appearance -----------------------------------------------------------------------------------------------------------------

/** The path of the frame in a box (w × h at the origin), as content operators ending in a space. */
export function shapePath(shape: Shape, w: number, h: number, inset: number): string {
    const [x0, y0, x1, y1] = [inset, inset, w - inset, h - inset];
    const n = num;
    if (shape === "square") return `${n(x0)} ${n(y0)} ${n(x1 - x0)} ${n(y1 - y0)} re `;
    if (shape === "rounded") {
        const r = Math.min(x1 - x0, y1 - y0) * 0.12, k = 0.5522847 * r;
        return `${n(x0 + r)} ${n(y0)} m ${n(x1 - r)} ${n(y0)} l ${n(x1 - r + k)} ${n(y0)} ${n(x1)} ${n(y0 + r - k)} ${n(x1)} ${n(y0 + r)} c ${n(x1)} ${n(y1 - r)} l ${n(x1)} ${n(y1 - r + k)} ${n(x1 - r + k)} ${n(y1)} ${n(x1 - r)} ${n(y1)} c ${n(x0 + r)} ${n(y1)} l ${n(x0 + r - k)} ${n(y1)} ${n(x0)} ${n(y1 - r + k)} ${n(x0)} ${n(y1 - r)} c ${n(x0)} ${n(y0 + r)} l ${n(x0)} ${n(y0 + r - k)} ${n(x0 + r - k)} ${n(y0)} ${n(x0 + r)} ${n(y0)} c h `;
    }
    const [cx, cy, rx, ry, k] = [(x0 + x1) / 2, (y0 + y1) / 2, (x1 - x0) / 2, (y1 - y0) / 2, 0.5522847];
    return `${n(cx + rx)} ${n(cy)} m ${n(cx + rx)} ${n(cy + k * ry)} ${n(cx + k * rx)} ${n(cy + ry)} ${n(cx)} ${n(cy + ry)} c ${n(cx - k * rx)} ${n(cy + ry)} ${n(cx - rx)} ${n(cy + k * ry)} ${n(cx - rx)} ${n(cy)} c ${n(cx - rx)} ${n(cy - k * ry)} ${n(cx - k * rx)} ${n(cy - ry)} ${n(cx)} ${n(cy - ry)} c ${n(cx + k * rx)} ${n(cy - ry)} ${n(cx + rx)} ${n(cy - k * ry)} ${n(cx + rx)} ${n(cy)} c h `;
}

/** How big the picture is drawn in a box, and where (bottom-left). */
export function fitBox(fit: Fit, bw: number, bh: number, pw: number, ph: number): { dw: number; dh: number; tx: number; ty: number } {
    const [dw, dh] = fit === "stretch" ? [bw, bh] : fit === "fit" ? (() => { const s = Math.min(bw / pw, bh / ph); return [pw * s, ph * s]; })() : (() => { const s = Math.max(bw / pw, bh / ph); return [pw * s, ph * s]; })();
    return { dw, dh, tx: (bw - dw) / 2, ty: (bh - dh) / 2 };
}

const fontRes = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>";
const helvWidth = (s: string, size: number) => { let w = 0; for (const ch of s) w += /[iljtfI.,:;'|!]/.test(ch) ? 0.28 : /[mwMW@]/.test(ch) ? 0.85 : /[A-Z0-9]/.test(ch) ? 0.66 : 0.52; return w * size; };
function wrapText(text: string, size: number, width: number): string[] {
    const lines: string[] = [];
    for (const para of text.split(/\r?\n/)) {
        let cur = "";
        for (const word of para.split(/(\s+)/)) { if (cur && helvWidth(cur + word, size) > width) { lines.push(cur.trimEnd()); cur = word.trimStart(); } else cur += word; }
        lines.push(cur.trimEnd());
    }
    return lines;
}
const pdfLit = (s: string) => "(" + [...s].map((ch) => { const c = ch.charCodeAt(0); return ch === "(" || ch === ")" || ch === "\\" ? "\\" + ch : c < 32 || c > 126 ? "\\" + (c < 256 ? c : 63).toString(8).padStart(3, "0") : ch; }).join("") + ")";

/** The content stream of the field's appearance: the picture in its frame, or the placeholder. `pic` is the picture's pixel size when one is in. */
export function appearanceContent(o: ImageFieldOpts, bw: number, bh: number, pic: { w: number; h: number } | null): string {
    let c = "q\n";
    const n = num;
    if (pic && pic.w > 0) {
        const { dw, dh, tx, ty } = fitBox(o.fit, bw, bh, pic.w, pic.h);
        c += `q ${shapePath(o.shape, bw, bh, 0)}W n\n`;
        c += `q ${n(dw)} 0 0 ${n(dh)} ${n(tx)} ${n(ty)} cm /Im0 Do Q Q\n`;
        if (o.border) c += `${n(o.border[0])} ${n(o.border[1])} ${n(o.border[2])} RG 1 w ${shapePath(o.shape, bw, bh, 0.5)}S\n`;
    } else {
        c += `0.95 0.96 0.98 rg ${shapePath(o.shape, bw, bh, 0.5)}f\n`;
        const b = o.border ?? [0.45, 0.5, 0.6];
        c += `${n(b[0])} ${n(b[1])} ${n(b[2])} RG 1 w [4 3] 0 d ${shapePath(o.shape, bw, bh, 0.5)}S [] 0 d\n`;
        const [cx, cy] = [bw / 2, bh * 0.58];
        const s = Math.min(40, Math.max(10, Math.min(bw, bh) * 0.28));
        c += `0.55 0.6 0.7 rg ${n(cx - s / 2)} ${n(cy - s * 0.32)} ${n(s)} ${n(s * 0.62)} re f ${n(cx - s * 0.2)} ${n(cy + s * 0.3)} ${n(s * 0.4)} ${n(s * 0.14)} re f\n`;
        c += `q 1 0 0 1 ${n(cx - s * 0.18)} ${n(cy - s * 0.18)} cm 1 1 1 rg ${shapePath("circle", s * 0.36, s * 0.36, 0)}f Q\n`;
        const size = Math.min(11, Math.max(7, bw / 11));
        let y = Math.max(4, cy - s * 0.32 - 6 - size);
        c += `BT /Helv ${n(size)} Tf 0.35 0.4 0.5 rg\n`;
        for (const l of wrapText(placeholderText(o), size, Math.max(size, bw - 10))) {
            c += `1 0 0 1 ${n(Math.max(2, (bw - helvWidth(l, size)) / 2))} ${n(y)} Tm ${pdfLit(l)} Tj\n`;
            y -= size * 1.2;
        }
        c += "ET\n";
    }
    return c + "Q\n";
}

// ---- reading and writing --------------------------------------------------------------------------------------------------------------

const pn = (s: string) => new PName(s);

export function optsToDict(o: ImageFieldOpts, state: { set: boolean; w?: number; h?: number; source?: string }): Dict {
    const d = new Dict(null);
    d.set("Fit", o.fit); d.set("Shape", o.shape); d.set("Placeholder", pdfText(o.placeholder)); d.set("MaxBytes", o.maxBytes); d.set("Types", o.types.join(",")); d.set("MaxPx", o.maxPx);
    if (o.border) d.set("Border", o.border);
    d.set("Set", state.set);
    if (state.set) { d.set("PicW", state.w ?? 0); d.set("PicH", state.h ?? 0); if (state.source) d.set("Source", pdfText(state.source)); }
    return d;
}

export function readImageOpts(d: Dict): { opts: ImageFieldOpts; set: boolean; w: number; h: number; source: string } | null {
    const g = d.dict("FGImg");
    if (!g) return null;
    const txt = (k: string) => { const v = g.get(k); return typeof v === "string" ? v : ""; };
    const fit = txt("Fit"), shape = txt("Shape");
    const b = g.arr("Border")?.map((x) => (typeof x === "number" ? x : 0));
    return {
        opts: { fit: fit === "fill" || fit === "stretch" ? fit : "fit", shape: shape === "rounded" || shape === "circle" ? shape : "square", placeholder: txt("Placeholder"), maxBytes: Math.max(0, g.num("MaxBytes")), types: txt("Types").split(",").map((t) => t.trim()).filter(Boolean), maxPx: g.has("MaxPx") ? Math.max(0, g.num("MaxPx")) : 1600, border: b && b.length === 3 ? [b[0]!, b[1]!, b[2]!] : null },
        set: g.get("Set") === true, w: g.num("PicW"), h: g.num("PicH"), source: txt("Source")
    };
}

/** Writes the picture as an image XObject (RGB, with a soft mask when it has see-through pixels) and the icon form that draws it. Returns the icon's number. */
async function writePicture(c: UpdateCtx, p: Pixels): Promise<{ icon: number; img: number }> {
    const n = p.w * p.h;
    const rgb = new Uint8Array(n * 3), alpha = new Uint8Array(n);
    let any = false;
    for (let i = 0; i < n; i++) { rgb[i * 3] = p.rgba[i * 4]!; rgb[i * 3 + 1] = p.rgba[i * 4 + 1]!; rgb[i * 3 + 2] = p.rgba[i * 4 + 2]!; alpha[i] = p.rgba[i * 4 + 3]!; if (alpha[i] !== 255) any = true; }
    let mask = 0;
    if (any) { mask = c.newRef(); await c.writeStream(mask, `/Type /XObject /Subtype /Image /Width ${p.w} /Height ${p.h} /ColorSpace /DeviceGray /BitsPerComponent 8`, alpha); }
    const img = c.newRef();
    await c.writeStream(img, `/Type /XObject /Subtype /Image /Width ${p.w} /Height ${p.h} /ColorSpace /DeviceRGB /BitsPerComponent 8${mask ? ` /SMask ${mask} 0 R` : ""}`, rgb);
    const icon = c.newRef();
    await c.writeStream(icon, `/Type /XObject /Subtype /Form /BBox [0 0 ${p.w} ${p.h}] /Resources << /XObject << /Im0 ${img} 0 R >> >>`, new TextEncoder().encode(`q ${p.w} 0 0 ${p.h} 0 0 cm /Im0 Do Q\n`));
    return { icon, img };
}

async function writeAppearance(c: UpdateCtx, widget: Dict, widgetNum: number, o: ImageFieldOpts, pic: { w: number; h: number } | null, imgNum?: number): Promise<void> {
    const r = (widget.arr("Rect") ?? [0, 0, 100, 100]).map((x) => (typeof x === "number" ? x : 0));
    const bw = Math.abs(r[2]! - r[0]!), bh = Math.abs(r[3]! - r[1]!);
    const ap = c.newRef();
    const icon = pic ? widget.dict("MK")?.raw("I") : null;
    const res = pic && imgNum ? `/XObject << /Im0 ${imgNum} 0 R >>` : pic && icon instanceof PRef ? `/XObject << /Im0 ${((): string => { const form = c.doc.xref.fetch(icon); const xo = form && "dict" in (form as object) ? (form as { dict: Dict }).dict.dict("Resources")?.dict("XObject") : null; const first = xo ? xo.raw(xo.keys()[0] ?? "") : null; return first instanceof PRef ? `${first.num} 0 R` : "null"; })()} >>` : `/Font << /Helv ${fontRes} >>`;
    await c.writeStream(ap, `/Type /XObject /Subtype /Form /BBox [0 0 ${num(bw)} ${num(bh)}] /Resources << ${res} >>`, new TextEncoder().encode(appearanceContent(o, bw, bh, pic)));
    const apd = new Dict(null);
    apd.set("N", new PRef(ap, 0));
    widget.set("AP", apd);
    c.put(widgetNum, widget);
}

export interface NewImageField { page: number; rect: [number, number, number, number]; name: string; opts: ImageFieldOpts; tooltip?: string; picture?: Pixels & { source: string }; events?: EventMap; link?: DataLink | null }

/** Adds an image field (a push button that shows a picture) to page `page` (0-based). */
export async function addImageField(c: UpdateCtx, f: NewImageField): Promise<PRef> {
    const num0 = c.newRef();
    const pref = c.pageRef(f.page);
    const w = new Dict(null);
    w.set("Type", pn("Annot")); w.set("Subtype", pn("Widget")); w.set("FT", pn("Btn")); w.set("Ff", 65536); w.set("T", pdfText(f.name)); w.set("F", 4);
    w.set("Rect", [...f.rect]);
    if (f.tooltip) w.set("TU", pdfText(f.tooltip));
    if (pref) w.set("P", pref);
    const mk = new Dict(null);
    mk.set("TP", 1);
    const iff = new Dict(null);
    iff.set("SW", pn("A")); iff.set("S", pn("A")); iff.set("A", [0.5, 0.5]);
    mk.set("IF", iff);
    w.set("MK", mk);
    const act = new Dict(null);
    act.set("S", pn("JavaScript")); act.set("JS", PICK_IMAGE_JS); act.set("FGAct", pn("PickImage"));
    w.set("A", act);
    let pic: { w: number; h: number } | null = null;
    let imgNum: number | undefined;
    if (f.picture) {
        const p = downscale(f.picture, f.opts.maxPx);
        const wp = await writePicture(c, p);
        imgNum = wp.img;
        mk.set("I", new PRef(wp.icon, 0));
        pic = { w: p.w, h: p.h };
        w.set("FGImg", optsToDict(f.opts, { set: true, w: p.w, h: p.h, source: f.picture.source }));
    } else w.set("FGImg", optsToDict(f.opts, { set: false }));
    if (f.events && Object.keys(f.events).length) applyEvents(w, f.events, (i) => c.pageRef(i), false);
    if (f.link) setWidgetLink(w, f.link);
    await writeAppearance(c, w, num0, f.opts, pic, imgNum);
    const ref = new PRef(num0, 0);
    addPageAnnots(c, f.page, [ref]);
    addAcroFields(c, [ref]);
    return ref;
}

export interface ImageFieldChange { opts?: ImageFieldOpts; /** A new picture, or null to take the picture out. */ picture?: (Pixels & { source: string }) | null; crop?: [number, number, number, number] }

/** Changes an existing image field (its settings and/or its picture) and redraws it. `widgetNum` is the object that carries /MK and /AP. */
export async function updateImageField(c: UpdateCtx, fieldNum: number, widgetNum: number, ch: ImageFieldChange, current: { opts: ImageFieldOpts; set: boolean; w: number; h: number; source: string }): Promise<void> {
    const field = c.copy(fieldNum);
    const widget = fieldNum === widgetNum ? field : c.copy(widgetNum);
    const opts = ch.opts ?? current.opts;
    let state = { set: current.set, w: current.w, h: current.h, source: current.source };
    let imgNum: number | undefined;
    const mk = new Dict(null);
    const oldMk = widget.dict("MK");
    if (oldMk) for (const k of oldMk.keys()) mk.set(k, oldMk.raw(k) as PObj);
    if (ch.picture === null) { mk.map.delete("I"); state = { set: false, w: 0, h: 0, source: "" }; }
    else if (ch.picture) {
        const p = downscale(ch.picture, opts.maxPx);
        const wp = await writePicture(c, p);
        imgNum = wp.img;
        mk.set("I", new PRef(wp.icon, 0));
        state = { set: true, w: p.w, h: p.h, source: ch.picture.source };
    }
    widget.set("MK", mk);
    field.set("FGImg", optsToDict(opts, state));
    if (field !== widget) c.put(fieldNum, field);
    await writeAppearance(c, widget, widgetNum, opts, state.set ? { w: state.w, h: state.h } : null, imgNum);
}
